import React from 'react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Privacy Policy
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] custom-scrollbar space-y-8">

          {/* Section: Introduction */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Introduction</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              Welcome to <strong>Scentral</strong> ("we," "our," or "us"). This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our fragrance
              collection tracking application (the "Service").
            </p>
          </div>

          {/* Section: Information We Collect */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Information We Collect
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2 text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Account Information</strong>: Username, email address, and encrypted password.</li>
                <li><strong>Profile Data</strong>: Preferences like theme and privacy settings.</li>
                <li><strong>Collection Data</strong>: Fragrance entries, notes, ratings, and comments.</li>
              </ul>
            </div>
          </div>

          {/* Section: How We Use Your Information */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              How We Use Your Information
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <p className="text-gray-900 dark:text-white text-sm mb-1">
                We use the collected information to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li>Provide and maintain the Service</li>
                <li>Create and manage your user account</li>
                <li>Store and organize your fragrance collection data</li>
                <li>Offer personalized autocomplete suggestions when typing</li>
                <li>Improve user experience and app performance</li>
                <li>Ensure the security and integrity of our Service</li>
              </ul>
            </div>
          </div>

          {/* Section: Data Storage and Security */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Data Storage and Security
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2 text-gray-600 dark:text-gray-400 text-sm">
              <ul className="list-disc list-inside space-y-1">
                <li>Your data is stored on secure servers with restricted access.</li>
                <li>We use industry-standard encryption, hashing, and token-based authentication.</li>
                <li>Security measures include rate limiting, input validation, and abuse prevention.</li>
              </ul>
            </div>
          </div>

          {/* Section: Your Rights and Choices */}
          <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
              Your Rights and Choices
            </h3>
            <div className="w-full sm:w-11/12 md:w-10/12 lg:w-8/12 mx-auto text-left space-y-2">
              <p className="text-gray-900 dark:text-white text-sm mb-1">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400 text-sm">
                <li><strong>Access</strong>: View all personal information we have about you.</li>
                <li><strong>Update</strong>: Modify your account information and preferences at any time.</li>
                <li><strong>Delete</strong>: Permanently delete your account and data.</li>
                <li><strong>Export</strong>: Download your collection data in CSV format.</li>
              </ul>
            </div>
          </div>
          {/* Section: Contact */}
          <div className="space-y-3 text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact Information</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              If you have any questions about this Privacy Policy or our data practices, contact us at{' '}
              <a
                href="mailto:support@scentral.app"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                support@scentral.app
              </a>.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-center">
            <Button onClick={onClose}>I Understand</Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicyModal;